// src/features/mech/plastics/playbook/PlasticsPlayBook.jsx
// Plastics Molding-specific PlayBook configuration component

import React from "react";
import { Box, Typography, useTheme } from "@mui/material";
import PerlSlider from "../../../../shared/components/PerlSlider";
import PerlAutocomplete from "../../../../shared/components/PerlAutocomplete";
import { theme } from "../../../../design/theme";

const { perlColors } = theme.custom;

/**
 * PlasticsPlayBook - Plastics Molding-specific playbook configuration
 * 
 * @param {Object} props
 * @param {Object} props.data - PlayBook data object with costParams: { cavities, cycleTime, machineTonnage, ohpRate, runnerType }
 * @param {Function} props.onChange - Callback: (field, value) => void
 */
export default function PlasticsPlayBook({ data = {}, onChange }) {
  // Extract costParams from data.costParams or data directly (for backward compatibility)
  const costParams = data.costParams || {
    cavities: data.cavities || 0,
    cycleTime: data.cycleTime || 0,
    machineTonnage: data.machineTonnage || 0,
    ohpRate: data.ohpRate || 0,
    runnerType: data.runnerType || "N/A",
  };
  const tonnageValue =
    (typeof costParams.machineTonnage === "number" || typeof costParams.machineTonnage === "string") &&
    /^[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:e[+-]?\d+)?$/i.test(String(costParams.machineTonnage).trim()) &&
    Number.isFinite(Number(costParams.machineTonnage))
      ? Number(costParams.machineTonnage) : 0;

  // Handler for parameter changes - updates costParams structure
  const handleParameterChange = (paramName, value) => {
    if (onChange) {
      // Update the costParams nested structure
      onChange("costParams", {
        ...costParams,
        [paramName]: value,
        _overrides: {
          ...costParams._overrides,
          [paramName]: paramName === "ohpRate" ||
            (paramName === "runnerType" ? value !== "N/A" : Number(value) > 0),
        },
      });
    }
  };

  // First row sliders (3 sliders: Cavities, Cycle Time, Machine Tonnage)
  const firstRowSliders = [
    {
      key: "cycleTime",
      label: "Cycle Time",
      value: parseFloat(costParams.cycleTime) || 0,
      min: 0,
      max: 120,
      step: 5,
      unit: " seconds",
      onChange: (e, v) => handleParameterChange("cycleTime", v),
    },
    {
      key: "machineTonnage",
      label: "Machine Tonnage",
      value: tonnageValue,
      min: 0,
      max: 500,
      step: 10,
      unit: " tons",
      onChange: (e, v) => handleParameterChange("machineTonnage", v),
    },
  ];

  // Runner type options
  const runnerTypeOptions = ["Hot Runner", "Cold Runner", "N/A"];

  return (
    <Box>
      {/* Process Parameters Title */}
      <Typography
        variant="subtitle2"
        fontWeight={600}
        sx={{ mb: 2, color: perlColors.textPrimary }}
      >
        Process Parameters
      </Typography>

      {/* First Row - 3 sliders only (Cavities, Cycle Time, Machine Tonnage) */}
      <Box
        sx={{
          backgroundColor: perlColors.surface,
          borderRadius: 2,
          p: 3,
          display: "flex",
          flexDirection: "column",
          gap: 3,
          mb: 2,
        }}
      >
        <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
          <Box sx={{ minWidth: 220, flex: "1 1 280px", maxWidth: "calc((100% - 48px) / 3)", mb: 2 }}>
            <PerlAutocomplete
              label="Cavities"
              value={costParams._overrides?.cavities ? String(costParams.cavities) : "Auto"}
              options={["Auto", "1", "2", "4", "6", "8", "16"]}
              onChange={(_, value) => handleParameterChange("cavities", value === "Auto" || !value ? 0 : Number(value))}
            />
            {!costParams._overrides?.cavities && costParams.cavities > 0 && (
              <Typography variant="caption" sx={{ color: perlColors.textSecondary }}>
                Calculated cavities: {costParams.cavities}
              </Typography>
            )}
          </Box>
          {firstRowSliders.map((slider) => (
            <Box
              key={slider.key}
              sx={{
                minWidth: 220,
                flex: "1 1 280px",
                maxWidth: "calc((100% - 48px) / 3)",
                mb: 2,
              }}
            >
              <PerlSlider
                label={slider.label}
                value={slider.value}
                min={slider.min}
                max={slider.max}
                step={slider.step}
                unit={slider.unit}
                onChange={slider.onChange}
              />
            </Box>
          ))}
        </Box>
        <Typography variant="caption" sx={{ color: perlColors.textSecondary }}>
          Select Auto for cavities, or set cycle time or tonnage to 0, to use calculated values.
          OHP is a percentage of material plus process cost; editing it replaces the calculated overhead amount.
        </Typography>

        {/* Second Row - OHP Rate and Runner Type */}
        <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
          {/* OHP Rate Slider */}
          <Box
            sx={{
              minWidth: 220,
              flex: "1 1 280px",
              maxWidth: "calc((100% - 48px) / 3)",
              mb: 2,
            }}
          >
            <PerlSlider
              label="OHP Rate"
              value={costParams.ohpRate ? parseFloat(parseFloat(costParams.ohpRate).toFixed(2)) : 0}
              min={0}
              max={30}
              step={0.5}
              unit="%"
              onChange={(e, v) => handleParameterChange("ohpRate", parseFloat(v.toFixed(2)))}
            />
          </Box>

          {/* Runner Type Dropdown */}
          <Box
            sx={{
              minWidth: 220,
              flex: "1 1 280px",
              maxWidth: "calc((100% - 48px) / 3)",
              mb: 2,
            }}
          >
            <PerlAutocomplete
              label="Runner Type"
              value={costParams.runnerType || "N/A"}
              onChange={(_, newValue) => {
                const runnerValue = newValue || "N/A";
                handleParameterChange("runnerType", runnerValue);
              }}
              options={runnerTypeOptions}
              placeholder="Select runner type"
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
